'use client'
import React, {useContext, useEffect, useState} from 'react';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import {Flex} from '@chakra-ui/layout';
import {Button, Input, Text} from '@chakra-ui/react';
import {IconButton} from '@chakra-ui/button';
import {MdAdd, MdCheck, MdClose, MdDelete} from 'react-icons/md';
import {forwardRef} from '@chakra-ui/system';
import usePersonaApi from '../../lib/usePersonaApi';
import { PersonaContext } from '../../lib/personaContext';
import useTranslation from '../../../lib/i18n/useTranslation';
const PersonaAsideUsefulLinksForm = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const {
    persona,
    personaId,
    refresh,
  } = useContext(PersonaContext);
  const {
    hideSave,
    onValidityChange,
    onSubmitted,
    onSubmitting,
    onCancel
  } = props;
  const [localFields, setLocalFields] = useState([]);
  const [hiddenFields, setHiddenFields] = useState([]);
  const {
    handleSubmit,
    errors,
    control,
    register,
    setValue,
    watch,
    reset,
    formState: { isSubmitting, isValid }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      usefulLinks: [{ title: '', url: '' }]
    }
  });
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'usefulLinks',
  });
  const watchedUsefulLinks = watch('usefulLinks');
  const personaApi = usePersonaApi();
  const isValidUrl = (string) => {
    if (!string || string.trim() === '') return false;
    let urlToTest = string.trim();
    if (!urlToTest.match(/^https?:\/\//)) {
      urlToTest = 'http://' + urlToTest;
    }
    try {
      new URL(urlToTest);
      return true;
    } catch (_) {
      return false;
    }
  };
  const onSubmit = async (data) => {
    try {
      if(onSubmitting) {
        onSubmitting();
      }
      if(persona.id && data.usefulLinks) {
        const filteredLinks = data.usefulLinks.filter((link, index) => 
          !hiddenFields.includes(index) && 
          link && link.title && link.title.trim() !== '' && 
          link.url && link.url.trim() !== '' && 
          isValidUrl(link.url)
        ).map(link => {
          let url = link.url.trim();
          if (!url.match(/^https?:\/\//)) {
            url = 'http://' + url;
          }
          return {
            ...link,
            url: url
          };
        });
        await personaApi.updateUsefulLinks(
          filteredLinks,
          persona.id
        )
        await refresh();
      }
      if(onSubmitted) {
        onSubmitted();
      }
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    if(persona.useful_links && persona.useful_links.length > 0) {
      reset({ usefulLinks: persona.useful_links });
      setLocalFields(persona.useful_links);
    } else {
      const defaultField = { title: '', url: '' };
      reset({ usefulLinks: [defaultField] });
      setLocalFields([defaultField]);
    }
    setHiddenFields([]);
  }, [persona, reset]);
  useEffect(() => {
    if(onValidityChange) {
      onValidityChange(isValid);
    }
  }, [isValid]);
  return (
    <>
      <Flex alignSelf={"stretch"}  w={"100%"} flexDirection={"column"} gap={2}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex flexDirection={"column"} gap={2}>
            {localFields.map((item, i) => 
              !hiddenFields.includes(i) ? (
                <Flex key={i} flexDirection={"column"} gap={2}>
                  <Controller
                    name={`usefulLinks[${i}].title`}
                    control={control}
                    defaultValue={item.title || ''}
                    render={({ field }) => (
                      <Input
                        placeholder={t('common.namePlaceholder')}
                        {...field}
                        width={"100%"}
                      />
                    )}
                  />
                  <Flex gap={2} alignItems={"center"} width={"100%"}>
                    <Controller
                      name={`usefulLinks[${i}].url`}
                      control={control}
                      defaultValue={item.url || ''}
                      render={({ field }) => (
                        <Input
                          placeholder='URL…'
                          {...field}
                          flex={1}
                        />
                      )}
                    />
                    <IconButton
                      variant="redOutline"
                      flexShrink={0}
                      icon={<MdDelete />}
                      onClick={() => {
                        if (localFields.length > 1) {
                          const newFields = [...localFields];
                          newFields.splice(i, 1);
                          setLocalFields(newFields);
                          remove(i);
                        } else {
                          setHiddenFields([...hiddenFields, i]);
                        }
                      }}
                      size="sm"
                    />
                  </Flex>
                  {watchedUsefulLinks?.[i]?.url && watchedUsefulLinks[i].url.trim() !== '' && !isValidUrl(watchedUsefulLinks[i].url) && (
                    <Text color="text.medium.red" fontSize="sm">
                      Please enter a valid URL
                    </Text>
                  )}
                </Flex>
              ) : null
            )}
            <Button
              type="button"
              alignSelf={"flex-start"}
              variant="blueTransparent"
              leftIcon={<MdAdd/>}
              onClick={() => {
                const newField = { title: '', url: '' };
                setLocalFields([...localFields, newField]);
                append(newField);
              }}
              isDisabled={false}
            >Add Link</Button>
            <Flex display={"flex"} gap={2}>
              {onCancel && (
                <IconButton
                  variant="greyOutline"
                  icon={<MdClose />}
                  onClick={onCancel}
                />
              )}
              <IconButton
                style={{
                  display: hideSave ? 'none' : 'inherit',
                }}
                ref={ref}
                icon={<MdCheck />}
                type={'submit'}
                aria-label='check'
                variant='blueOutline'
                isLoading={isSubmitting}
                isDisabled={!isValid}
              />
            </Flex>
          </Flex>
        </form>
      </Flex>
    </>
  );
});
PersonaAsideUsefulLinksForm.displayName = 'PersonaAsideUsefulLinksForm';
export default PersonaAsideUsefulLinksForm; 